<template>
  <div
    :class="rootClasses"
    data-cy="ds-accordion"
  >
    <div
      :class="headerClasses"
      data-cy="ds-accordion__header"
      @click="onHeaderClick"
    >
      <span
        :id="labelId"
        :class="DS_ACCORDION_CLASSES.Label"
        data-cy="ds-accordion__label"
      >
        {{ props.label }}
      </span>

      <span
        v-if="hasSubtitle"
        :class="DS_ACCORDION_CLASSES.Subtitle"
        data-cy="ds-accordion__subtitle"
      >
        {{ props.subtitle }}
      </span>

      <div
        v-if="hasSuffix"
        :class="DS_ACCORDION_CLASSES.Suffix"
        data-cy="ds-accordion__suffix"
      >
        <slot name="suffix" />
      </div>

      <DSButton
        v-if="isCollapsible"
        :class="DS_ACCORDION_CLASSES.Toggle"
        variant="tertiary"
        :size="toggleSize"
        :prefix-icon="isExpanded ? 'chevron-top' : 'chevron-down'"
        :aria-labelledby="labelId"
        :aria-expanded="isExpanded"
        :aria-controls="contentId"
        data-cy="ds-accordion__toggle"
      />

      <DSDivider
        v-if="props.withDivider"
        :class="DS_ACCORDION_CLASSES.Divider"
        data-cy="ds-accordion__divider"
      />
    </div>

    <div
      :id="contentId"
      :class="DS_ACCORDION_CLASSES.Content"
      :role="isCollapsible ? 'region' : undefined"
      :aria-labelledby="isCollapsible ? labelId : undefined"
      :hidden="isCollapsible && !isExpanded"
      data-cy="ds-accordion__content"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSDivider from "@/src/design_system/layouts_and_containers/divider/components/DSDivider.vue";
import {
  DS_ACCORDION_CLASSES,
  DS_ACCORDION_EXPANDED_STORAGE_KEY,
  DS_ACCORDION_HEADER_INTERACTIVE_SELECTOR,
  DS_ACCORDION_SIZE_TO_BUTTON_SIZE_MAP,
  DS_ACCORDION_SIZE_TO_CLASS_MAP,
} from "@/src/design_system/layouts_and_containers/accordion/constants";
import { DS_ACCORDION_SIZE } from "@/src/design_system/layouts_and_containers/accordion/types/size";
import type { DSAccordionSize } from "@/src/design_system/layouts_and_containers/accordion/types/size";
import type { DSAccordionProps } from "@/src/design_system/layouts_and_containers/accordion/types/props";
import type { DSAccordionEmits } from "@/src/design_system/layouts_and_containers/accordion/types/emits";
import { usePersistentPreference } from "@/src/design_system/shared/composables/usePersistentPreference";
import { getPersistenceKey } from "@/src/design_system/shared/utils/persistenceKey";
import { generateUUID } from "@/src/shared/utils/uuid";
import { hasSlotContent } from "@/src/shared/utils/slots";

const props = withDefaults(defineProps<DSAccordionProps>(), {
  subtitle: undefined,
  size: DS_ACCORDION_SIZE.Medium,
  collapsible: true,
  expanded: undefined,
  defaultExpanded: true,
  persistenceKey: undefined,
  withDivider: false,
});

const emits = defineEmits<DSAccordionEmits>();
const slots = useSlots();
const instanceId = generateUUID();
const labelId = `ds-accordion-label-${instanceId}`;
const contentId = `ds-accordion-content-${instanceId}`;

const localExpanded = ref(props.defaultExpanded);
const expandedModel = computed({
  get: () => props.expanded ?? localExpanded.value,
  set: expanded => {
    if (expanded === (props.expanded ?? localExpanded.value)) return;
    if (props.expanded === undefined) localExpanded.value = expanded;
    emits("update:expanded", expanded);
  },
});
const isExpanded = usePersistentPreference({
  storageKey: DS_ACCORDION_EXPANDED_STORAGE_KEY,
  key: getPersistenceKey(props.persistenceKey),
  value: expandedModel,
  validate: (value): value is boolean => typeof value === "boolean",
});

const normalizedSize = computed<DSAccordionSize>(() =>
  Object.values(DS_ACCORDION_SIZE).includes(props.size) ? props.size : DS_ACCORDION_SIZE.Medium
);
const rootClasses = computed(() => [DS_ACCORDION_CLASSES.Root, DS_ACCORDION_SIZE_TO_CLASS_MAP[normalizedSize.value]]);
const toggleSize = computed(() => DS_ACCORDION_SIZE_TO_BUTTON_SIZE_MAP[normalizedSize.value]);
const hasSubtitle = computed(() => Boolean(props.subtitle?.trim()));
const hasSuffix = computed(() => hasSlotContent(slots, "suffix"));
const isCollapsible = computed(() => props.collapsible && Boolean(props.label?.trim()));

const headerClasses = computed(() => [
  DS_ACCORDION_CLASSES.Header,
  { [DS_ACCORDION_CLASSES.HeaderInteractive]: isCollapsible.value },
]);

const isSuffixControlClick = (event: MouseEvent) => {
  const control = (event.target as Element).closest(DS_ACCORDION_HEADER_INTERACTIVE_SELECTOR);
  return control !== null && !control.classList.contains(DS_ACCORDION_CLASSES.Toggle);
};

const onHeaderClick = (event: MouseEvent) => {
  if (!isCollapsible.value || isSuffixControlClick(event)) return;
  isExpanded.value = !isExpanded.value;
};
</script>
